import React, { useState, useEffect, useMemo } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  TablePagination,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  ListItemText,
  FormHelperText,
  TableContainer,
  Paper,
  TableSortLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from '@firebase/firestore';
import { db, storage, auth } from '../firebase-config';
import { signOut } from 'firebase/auth';
import { uploadBytes, getDownloadURL, ref as storageRef, deleteObject } from 'firebase/storage';
import LocationAutocomplete from '../LocationAutocomplete';

const useCollection = (name) => {
  const [items, setItems] = useState([]);

  const load = async () => {
    const snap = await getDocs(collection(db, name));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems(data);
  };

  useEffect(() => { load(); }, [name]);

  const add = async (data, file) => {
    const docRef = await addDoc(collection(db, name), data);
    if (file) {
      const imageRef = storageRef(storage, `${name}/${docRef.id}.jpg`);
      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);
      await updateDoc(docRef, { image_url: url });
    }
    load();
  };

  const update = async (id, data, file) => {
    const docRef = doc(db, name, id);
    const existing = items.find((i) => i.id === id);
    if (file) {
      if (existing?.image_url) {
        try {
            const path = decodeURIComponent(existing.image_url.split('/o/')[1].split('?')[0]);
            await deleteObject(storageRef(storage, path));
        } catch(e) {
            console.error("Error deleting old image: ", e);
        }
      }
      const imageRef = storageRef(storage, `${name}/${id}.jpg`);
      await uploadBytes(imageRef, file);
      data.image_url = await getDownloadURL(imageRef);
    }
    await updateDoc(docRef, data);
    load();
  };

  const remove = async (id) => {
    const existing = items.find((i) => i.id === id);
    if (existing?.image_url) {
        try {
            const path = decodeURIComponent(existing.image_url.split('/o/')[1].split('?')[0]);
            await deleteObject(storageRef(storage, path));
        } catch(e) {
            console.error("Error deleting image: ", e);
        }
    }
    await deleteDoc(doc(db, name, id));
    load();
  };

  return { items, add, update, remove, load };
};

// --- Sorting Utility Functions ---
function descendingComparator(a, b, orderBy) {
    const [parentA, childA] = orderBy.includes('.') ? orderBy.split('.') : [orderBy, null];
    let valA = childA ? a[parentA]?.[childA] : a[parentA];
    let valB = childA ? b[parentA]?.[childA] : b[parentA];

    if (valB < valA) {
        return -1;
    }
    if (valB > valA) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

// --- Enhanced Table Header with Sorting ---
const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort, headCells, onSelectAllClick, numSelected, rowCount } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{ 'aria-label': 'select all items' }}
                    />
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.name}
                        sortDirection={orderBy === headCell.name ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.name}
                            direction={orderBy === headCell.name ? order : 'asc'}
                            onClick={createSortHandler(headCell.name)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell>Image</TableCell>
                <TableCell>Actions</TableCell>
            </TableRow>
        </TableHead>
    );
};


const ManageList = ({ name, fields, collectionLabel }) => {
  const { items, add, update, remove, load } = useCollection(name);

  const uniqueTags = name === 'well-being-resources'
    ? Array.from(new Set(items.map(item => item.tag).filter(Boolean))).sort()
    : [];

  const uniqueTypes = name === 'well-being-services'
    ? Array.from(new Set(items.flatMap(item => item.types || []))).sort()
    : [];

  const getInitialState = () => {
      const initialState = fields.reduce((acc, f) => {
        if (f.name.includes('.')) {
            const [parent, child] = f.name.split('.');
            if (!acc[parent]) acc[parent] = {};
            acc[parent][child] = '';
        } else {
            acc[f.name] = f.type === 'boolean' ? false : f.type === 'array' ? [] : '';
        }
        return acc;
      }, {});
      if(name === 'well-being-services'){
          initialState.coords = null;
      }
      if (name === 'well-being-playlists') {
        initialState.playlist_type = 'Spotify';
      }
      return initialState;
  };

  const [form, setForm] = useState(getInitialState());
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(fields[0].name);
  const rowsPerPage = 15;

  const capitalized = collectionLabel || (name.split('-').pop().slice(0, -1));

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= items.length) {
      setPage(Math.max(0, Math.ceil(items.length / rowsPerPage) - 1));
    }
  }, [items.length, page, rowsPerPage]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = items.map((n) => n.id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;

  const handleDeleteSelected = async () => {
    await Promise.all(selectedIds.map((id) => remove(id)));
    setSelectedIds([]);
  };

  const sanitizeUrl = (value) => {
    let val = value.trim();
    if (!val) return '';
    if (!/^[a-zA-Z][a-zA-Z0-9+-.]*:\/\//.test(val)) {
      val = `https://${val}`;
    }
    try {
      new URL(val);
    } catch (err) {
      return null;
    }
    return val;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form };
    if (data.types && typeof data.types === 'string') {
        data.types = data.types.split(',').map(s => s.trim());
    }
    for (const f of fields) {
        if (f.name.toLowerCase().includes('url') && data[f.name]) {
            const sanitized = sanitizeUrl(data[f.name]);
            if (sanitized === null) {
                alert(`Please enter a valid URL for ${f.label}`);
                return;
            }
            data[f.name] = sanitized;
        }
    }
    if (editingId) {
      update(editingId, data, file);
    } else {
      add(data, file);
    }
    handleClose();
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    if (f) {
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleDeleteImage = async () => {
    if (!editingId) return;
    const item = items.find((i) => i.id === editingId);
    if (item?.image_url) {
        try {
            const path = decodeURIComponent(item.image_url.split('/o/')[1].split('?')[0]);
            await deleteObject(storageRef(storage, path));
            await updateDoc(doc(db, name, editingId), { image_url: '' });
            await load();
        } catch (e) {
            console.error("Error deleting image: ", e);
        }
    }
    setPreview(null);
    setFile(null);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setForm(getInitialState());
    setFile(null);
    setPreview(null);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    const formState = {};
    fields.forEach(f => {
        if (f.name.includes('.')) {
            const [parent, child] = f.name.split('.');
            if (!formState[parent]) formState[parent] = {};
            formState[parent][child] = item[parent]?.[child] || '';
        } else if (f.type === 'array') {
            formState[f.name] = Array.isArray(item[f.name]) ? item[f.name] : [];
        } else {
            formState[f.name] = item[f.name] || (f.type === 'boolean' ? false : '');
        }
    });
    if(name === 'well-being-services'){
        formState.coords = item.coords || null;
    }
    setForm(formState);
    setEditingId(item.id);
    setPreview(item.image_url || null);
    setFile(null);
    setDialogOpen(true);
  }

  const handleFormChange = (e) => {
      const { name, value, type, checked } = e.target;
      if (name.includes('.')) {
          const [parent, child] = name.split('.');
          setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value }}));
      } else {
          setForm(prev => ({...prev, [name]: type === 'checkbox' ? checked : value}));
      }
  }

  const sortedItems = useMemo(() => stableSort(items, getComparator(order, orderBy)), [items, order, orderBy]);

  return (
    <Box>
      <Box mb={2}>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            setDialogOpen(true);
            setForm(getInitialState());
            setEditingId(null);
            setFile(null);
            setPreview(null);
          }}
        >
          Add {capitalized}
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="small"
          color="error"
          sx={{ ml: 1 }}
          onClick={handleDeleteSelected}
          disabled={selectedIds.length === 0}
        >
          Delete Selected ({selectedIds.length})
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
          '& .MuiDialog-paper': {
            mt: '10vh',
          },
        }}
      >
        <Box component="form" onSubmit={handleSubmit} p={2}>
          <DialogTitle>{editingId ? `Edit ${capitalized}` : `Add ${capitalized}`}</DialogTitle>
          <DialogContent>
            {fields.map((f) => {
                if(f.type === 'boolean') {
                    return <FormControlLabel control={<Checkbox checked={!!form[f.name]} onChange={handleFormChange} name={f.name}/>} label={f.label} key={f.name}/>
                }
                if (f.name === 'location' && (name === 'well-being-services' || name === 'well-being-resources')) {
                    const handleChange = (val) => {
                        const newFormState = { ...form, location: val.location };
                        if (name === 'well-being-services') {
                            newFormState.coords = val.coords;
                        }
                        setForm(newFormState);
                    };
                    return <LocationAutocomplete key={f.name} label={f.label} value={form.location} onChange={handleChange} />
                }
                if (f.name === 'types' && name === 'well-being-services') {
                  return (
                    <FormControl key={f.name} fullWidth margin="dense" size="small">
                      <InputLabel>{f.label}</InputLabel>
                      <Select
                        multiple
                        name={f.name}
                        value={form.types || []}
                        onChange={handleFormChange}
                        input={<OutlinedInput label={f.label} />}
                        renderValue={(selected) => selected.join(', ')}
                      >
                        {uniqueTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            <Checkbox checked={(form.types || []).indexOf(type) > -1} />
                            <ListItemText primary={type} />
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Select multiple types</FormHelperText>
                    </FormControl>
                  );
                }
                if (f.name === 'tag' && name === 'well-being-resources') {
                  return (
                    <TextField
                      key={f.name}
                      select
                      label={f.label}
                      value={form.tag || ''}
                      onChange={handleFormChange}
                      name={f.name}
                      fullWidth
                      margin="dense"
                      size="small"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {uniqueTags.map((tag) => (
                        <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                      ))}
                    </TextField>
                  );
                }
                if (f.name === 'playlist_type' && name === 'well-being-playlists') {
                  return (
                    <TextField
                      key={f.name}
                      label={f.label}
                      value={form.playlist_type || 'Spotify'}
                      name={f.name}
                      fullWidth
                      margin="dense"
                      size="small"
                      disabled
                    />
                  );
                }
                const [parent, child] = f.name.includes('.') ? f.name.split('.') : [f.name, null];
                return (
                  <TextField
                    key={f.name}
                    label={f.label}
                    value={child ? form[parent]?.[child] || '' : form[parent] || ''}
                    onChange={handleFormChange}
                    name={f.name}
                    fullWidth
                    margin="dense"
                    size="small"
                    multiline={f.name === 'description'}
                    rows={f.name === 'description' ? 3 : 1}
                  />
                )
            })}
            {preview && (
              <img
                src={preview}
                alt="preview"
                style={{ height: 60, display: 'block', marginBottom: 8 }}
              />
            )}
            <input type="file" onChange={handleFileChange} />
            {editingId && preview && (
              <Button onClick={handleDeleteImage} size="small" sx={{ ml: 1 }}>
                Delete Image
              </Button>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingId ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="small" stickyHeader>
              <EnhancedTableHead
                  numSelected={selectedIds.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={items.length}
                  headCells={fields}
              />
              <TableBody>
                {sortedItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => {
                    const isItemSelected = isSelected(item.id);
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={(event) => handleClick(event, item.id)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                        sx={{ '&:nth-of-type(odd)': { backgroundColor: (theme) => theme.palette.action.hover } }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                          />
                        </TableCell>
                        {fields.map((f) => {
                          const [parent, child] = f.name.includes('.') ? f.name.split('.') : [f.name, null];
                          let value = child ? item[parent]?.[child] : item[parent];

                          if(f.type === 'boolean') {
                              return <TableCell key={f.name}><Checkbox checked={!!value} disabled/></TableCell>
                          }
                          if (Array.isArray(value)) {
                              value = value.join(', ');
                          }
                          const cellStyle = (f.name === 'description' || f.name === 'types') ? { minWidth: 250 } : {};
                          return (
                          <TableCell key={f.name} sx={cellStyle}>{value}</TableCell>
                        )})}
                        <TableCell>
                          {item.image_url && (
                            <img src={item.image_url} alt="" style={{ height: 60, borderRadius: 4 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleEdit(item);}}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <Button
                            onClick={(e) => { e.stopPropagation(); remove(item.id);}}
                            size="small" sx={{ ml: 1 }}
                            color="error"
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={items.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </Paper>
    </Box>
  );
};

const AdminDashboard = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%', mt: 2, px: 0}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom textAlign="center">
          Admin Dashboard
        </Typography>
        <Button onClick={() => signOut(auth)} variant="outlined" size="small">
          Log Out
        </Button>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} centered>
        <Tab label="Services" />
        <Tab label="Resources" />
        <Tab label="Playlists" />
      </Tabs>
      <Box mt={2}>
        {tab === 0 && (
          <ManageList
            name="well-being-services"
            collectionLabel="Service"
            fields={[
              { name: 'business_name', label: 'Business Name' },
              { name: 'individual_name', label: 'Individual Name' },
              { name: 'is_individual', label: 'Is Individual', type: 'boolean' },
              { name: 'description', label: 'Description' },
              { name: 'location', label: 'Location' },
              { name: 'url', label: 'URL' },
              { name: 'types', label: 'Types', type: 'array' },
              { name: 'contact.email', label: 'Email' },
              { name: 'contact.phone', label: 'Phone' },
            ]}
          />
        )}
        {tab === 1 && (
          <ManageList
            name="well-being-resources"
            collectionLabel="Resource"
            fields={[
              { name: 'name', label: 'Name' },
              { name: 'description', label: 'Description' },
              { name: 'tag', label: 'Tag' },
              { name: 'location', label: 'Location' },
              { name: 'resource_url', label: 'Resource URL' }
            ]}
          />
        )}
        {tab === 2 && (
          <ManageList
            name="well-being-playlists"
            collectionLabel="Playlist"
            fields={[
              { name: 'summary', label: 'Summary' },
              { name: 'description', label: 'Description' },
              { name: 'playlist_type', label: 'Playlist Type' },
              { name: 'asset_type', label: 'Asset Type' },
              { name: 'embed_code', label: 'Embed Code' }
            ]}
          />
        )}
      </Box>
    </Box>
  );
};

export default AdminDashboard;